import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TableList, Typography, TextButton, InfoIcon, useIsMounted } from 'seagull';
import { wait } from "../../../infra/utils";
import { InvoiceQboIntegrationStatus } from "../../../components/InvoiceQboIntegrationStatus";
import { PayoutQboIntegrationStatus } from "../PayoutQboIntegrationStatus/PayoutQboIntegrationStatus";
import PayoutQboSection from "../PayoutsQboSection/PayoutQboSection";
import { useAppConfig, useStore, useStoreActions } from "../../../store/store-context";
import { selectors } from "../../../store/state-selectors";
import { getURLWithFullPageInvoice } from "../../../components/FullPageInvoice/FullPageInvoice.utils";
import styles from "./PayoutsDashboardRow.module.scss";
import { timeModel } from "../../../models";
import compact from 'lodash/compact';
import { getStatusSkin, getStatusText } from "./PayoutsDashboardRow.utils";
import { makeSentryIgnoredError } from "../../../components/ErrorBoundaryPage";
import PayoutFailedCallout from "../PayoutFailureMessage/PayoutFailedCallout";
const POLLING_TIMEOUT = 2 * 60 * 1000; // 2min in ms

function AmountDisplay(props) {
  if (!props.amount) return null;
  return /*#__PURE__*/React.createElement(Typography, {
    ellipsis: true,
    skin: "secondary",
    "data-testid": props.testid
  }, props.amount.display());
}
function FeesCell(_ref) {
  let {
    invoice
  } = _ref;
  return /*#__PURE__*/React.createElement("div", {
    className: styles.fees,
    "data-testid": "fees-".concat(invoice.id)
  }, /*#__PURE__*/React.createElement(AmountDisplay, {
    amount: invoice.totalFees
  }), !invoice.totalFees.isZero() && /*#__PURE__*/React.createElement(InfoIcon, {
    "data-testid": "fees-tooltip-".concat(invoice.id),
    info: /*#__PURE__*/React.createElement("div", {
      className: styles.feesTooltip
    }, /*#__PURE__*/React.createElement(Typography, {
      skin: "invertedStandard",
      weight: "bold"
    }, "Transaction fees"), !invoice.fee.isZero() && /*#__PURE__*/React.createElement("div", {
      "data-testid": "fees-tooltip-anchor-fee-".concat(invoice.id)
    }, invoice.fee.display(), " Anchor"), !invoice.processingFee.isZero() && /*#__PURE__*/React.createElement("div", {
      "data-testid": "fees-tooltip-processing-fee-".concat(invoice.id)
    }, invoice.processingFee.display(), " Credit card"), !invoice.serviceFeeRefund.isZero() && /*#__PURE__*/React.createElement("div", {
      "data-testid": "fees-tooltip-service-fee-refund-".concat(invoice.id)
    }, invoice.serviceFeeRefund.neg().display(), " Anchor fee refund"), !invoice.customerProcessingFee.isZero() && /*#__PURE__*/React.createElement("div", {
      "data-testid": "fees-tooltip-customer-processing-fee-".concat(invoice.id)
    }, invoice.customerProcessingFee.display(), " Credit card fees returned to client"), !invoice.disputeFee.isZero() && /*#__PURE__*/React.createElement("div", {
      "data-testid": "fees-tooltip-dispute-fee-".concat(invoice.id)
    }, invoice.disputeFee.display(), " chargebacks fees for a dispute received on invoice", ' ', invoice.formattedNumber))
  }));
}
export default function PayoutsDashboardRow(props) {
  const {
    index,
    invoiceHeaders
  } = props;
  const showQboIntegration = useStore(selectors.integration.showQboIntegration);
  const {
    api
  } = useStoreActions();
  const isMounted = useIsMounted();
  const {
    qboPollingInterval
  } = useAppConfig();
  const [hasError, setHasError] = useState(false);
  const testid = "payments-row-".concat(index);
  const [payout, setPayout] = useState(props.payout);
  useEffect(() => setPayout(props.payout), [props.payout]);
  const columnDefs = [{
    width: 120
  }, {
    width: 80
  }, {
    width: [140, 180]
  }, {
    width: [90, 100]
  }, {
    width: [90, 100]
  }, {
    width: 100,
    hAlign: 'end'
  }, {
    width: 80,
    hAlign: 'end'
  }, {
    width: 110,
    hAlign: 'end'
  }];
  const headerItems = invoiceHeaders.map(text => ({
    value: text
  }));
  async function reloadPayout() {
    const fetchPayout = async () => {
      return api.payout.getById(payout.id()).catch(err => {
        isMounted() && setHasError(true);
        throw err;
      });
    };
    const start = Date.now();
    let data = await fetchPayout();

    // Keep polling until the invoice reaches a stable integration status.
    while (isMounted() && data.integration() && !data.integration().isStableStatus() && Date.now() - start < POLLING_TIMEOUT) {
      await wait(qboPollingInterval);
      data = await fetchPayout();
    }
    isMounted() && setPayout(data);
    return data;
  }
  if (hasError) {
    throw makeSentryIgnoredError('Payout fetch error');
  }
  return /*#__PURE__*/React.createElement(TableList.Row, {
    as: "button",
    "data-testid": testid,
    "data-heapid": "payment",
    rowId: payout.id(),
    items: compact([{
      value: /*#__PURE__*/React.createElement(Typography, {
        weight: "light"
      }, payout.formattedCreatedAt())
    }, showQboIntegration && {
      value: /*#__PURE__*/React.createElement(PayoutQboIntegrationStatus, {
        testid: "".concat(testid, "-qbo"),
        integration: payout.integration()
      })
    }, {
      value: /*#__PURE__*/React.createElement(Typography, {
        weight: "normal",
        capitalize: true,
        "data-testid": "".concat(testid, "-status"),
        skin: getStatusSkin(payout)
      }, getStatusText(payout))
    }, {
      value: /*#__PURE__*/React.createElement(AmountDisplay, {
        amount: payout.amount()
      })
    }]),
    expandedContent: () => /*#__PURE__*/React.createElement("div", {
      className: styles.expandedWrapper
    }, /*#__PURE__*/React.createElement(PayoutFailedCallout, {
      payout: payout,
      testid: "".concat(testid, "-failure-reason")
    }), payout.isPaid() && showQboIntegration && !!payout.integration() && /*#__PURE__*/React.createElement(PayoutQboSection, {
      reloadPayout: reloadPayout,
      testid: "".concat(testid, "-qbo-section"),
      payoutId: payout.id(),
      payoutIntegration: payout.integration()
    }), /*#__PURE__*/React.createElement(TableList.Provider, {
      columnDefs: columnDefs,
      variant: "minimal"
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.expandedHeader
    }, /*#__PURE__*/React.createElement(TableList.Header, {
      items: headerItems,
      color: "transparent"
    })), /*#__PURE__*/React.createElement("div", {
      "data-testid": "invoice-rows"
    }, payout.invoices().map((invoice, idx) => /*#__PURE__*/React.createElement(TableList.Row, {
      key: invoice.id + invoice.type,
      "data-testid": "invoice-row-".concat(invoice.id),
      items: compact([{
        value: /*#__PURE__*/React.createElement("div", {
          className: styles.invoiceNumber
        }, /*#__PURE__*/React.createElement(TextButton, {
          as: Link,
          "data-testid": "".concat(testid, "-invoice-link-").concat(idx),
          "data-heapid": "payment-invoice-link",
          to: getURLWithFullPageInvoice({
            invoiceId: invoice.id,
            relationshipId: invoice.relationshipId
          })
        }, invoice.formattedNumber), showQboIntegration && /*#__PURE__*/React.createElement(InvoiceQboIntegrationStatus, {
          testid: "".concat(testid, "-invoice-qbo-").concat(idx),
          integration: invoice.integration
        }))
      }, {
        value: invoice.type
      }, {
        value: /*#__PURE__*/React.createElement(Typography, {
          ellipsis: true,
          skin: "secondary"
        }, invoice.customer)
      }, {
        value: timeModel.formatDate(invoice.createdOn)
      }, {
        value: timeModel.formatDate(invoice.dueDate)
      }, {
        value: /*#__PURE__*/React.createElement(AmountDisplay, {
          amount: invoice.totalAmount
        })
      }, {
        value: /*#__PURE__*/React.createElement(FeesCell, {
          invoice: invoice
        })
      }, {
        value: /*#__PURE__*/React.createElement(AmountDisplay, {
          amount: invoice.depositAmount,
          testid: "deposit-amount-".concat(invoice.id)
        })
      }])
    })))))
  });
}